import { redirect, replace } from "react-router";

import { readQueryParams } from "@/hooks/useQueryParam";

/**
 * Redirects from a route loader without leaving the requested URL in history. In-app navigations keep their own
 * push or replace. On a page load or back/forward the browser is already on the requested URL, and React Router
 * would push the redirect after it, so replace that entry instead.
 */
export function redirectFromLoader(request: Request, to: string): Response {
  const requested = new URL(request.url);
  const isBrowserOnRequestedUrl =
    requested.pathname === window.location.pathname && requested.search === window.location.search;

  return isBrowserOnRequestedUrl ? replace(to) : redirect(to);
}

/**
 * `url` with the stored params added, when the URL has none of `ownKeys` itself. Pages that remember their filters
 * for the tab redirect there from their loader, so the first render already queries the restored filters.
 */
export function getUrlWithStoredParams(
  url: URL,
  ownKeys: readonly string[],
  stored: Readonly<Record<string, unknown>>,
): string | undefined {
  const queryParams = readQueryParams<Record<string, string>>(url.searchParams);
  if (ownKeys.some((key) => queryParams[key] != null)) {
    return undefined;
  }

  const search = new URLSearchParams(url.search);
  for (const [key, value] of Object.entries(stored)) {
    const trimmedValue = getStoredParamValue(value);
    if (trimmedValue) {
      search.set(key, trimmedValue);
    }
  }

  const restoredSearch = search.toString();
  if (restoredSearch === url.searchParams.toString()) {
    return undefined;
  }

  return `${url.pathname}?${restoredSearch}`;
}

// Pages store their parsed query params, which hold a repeated key as an array. The URL writes lists comma-joined.
function getStoredParamValue(value: unknown): string | undefined {
  const joined = Array.isArray(value) ? value.filter((item) => typeof item === "string").join(",") : value;

  return typeof joined === "string" ? joined.trim() || undefined : undefined;
}
